<template>
    <div v-if="isDev" class="dev-toolbar-wrapper">
        <!-- Collapsed FAB -->
        <v-btn
            v-if="!expanded"
            icon
            :color="isTabOverride ? 'deep-purple' : 'warning'"
            size="small"
            class="dev-toolbar-fab"
            @click="expanded = true"
            :title="isTabOverride ? `Tab-Simulation: ${tabUser}` : 'Dev Simulator'"
        >
            <v-icon>{{ isTabOverride ? 'mdi-account-multiple' : 'mdi-bug' }}</v-icon>
        </v-btn>

        <!-- Expanded Panel -->
        <v-card v-if="expanded" class="dev-toolbar-card" elevation="8" width="340">
            <v-card-title class="d-flex align-center py-2 px-3" :class="isTabOverride ? 'bg-deep-purple' : 'bg-warning'">
                <v-icon size="small" class="mr-2">mdi-bug</v-icon>
                <span class="text-body-2 font-weight-bold">Dev Simulator</span>
                <v-spacer/>
                <v-btn icon variant="text" size="x-small" @click="resetAll" title="Reset">
                    <v-icon size="small">mdi-refresh</v-icon>
                </v-btn>
                <v-btn icon variant="text" size="x-small" @click="expanded = false">
                    <v-icon size="small">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-3">
                <!-- Tab Override Banner -->
                <v-alert
                    v-if="isTabOverride"
                    type="info"
                    density="compact"
                    variant="tonal"
                    class="mb-3"
                >
                    <div class="text-caption">
                        Tab-Simulation aktiv: <strong>{{ tabUser }}</strong>
                    </div>
                    <v-btn
                        size="x-small"
                        variant="text"
                        color="error"
                        class="mt-1"
                        @click="clearTabOverride"
                    >
                        Tab-Simulation aufheben
                    </v-btn>
                </v-alert>

                <!-- User Simulation -->
                <div class="text-caption font-weight-bold mb-1">Benutzer simulieren</div>
                <v-select
                    v-model="simulatedUser"
                    :items="availableUsers"
                    item-title="username"
                    item-value="username"
                    density="compact"
                    variant="outlined"
                    clearable
                    placeholder="Kein Override"
                    hide-details
                    class="mb-3"
                    :menu-props="{ zIndex: 10000 }"
                    @update:model-value="onSimulationChange"
                />

                <!-- Open as another user in new tab -->
                <div class="text-caption font-weight-bold mb-1">In neuem Tab simulieren</div>
                <div class="d-flex ga-1 mb-3">
                    <v-select
                        v-model="newTabUser"
                        :items="availableUsers"
                        item-title="username"
                        item-value="username"
                        density="compact"
                        variant="outlined"
                        placeholder="User wählen"
                        hide-details
                        class="flex-grow-1"
                        :menu-props="{ zIndex: 10000 }"
                    />
                    <v-btn
                        icon
                        size="small"
                        color="deep-purple"
                        :disabled="!newTabUser"
                        @click="openAsUserInNewTab"
                        title="In neuem Tab öffnen"
                    >
                        <v-icon size="small">mdi-open-in-new</v-icon>
                    </v-btn>
                </div>

                <!-- Role Simulation -->
                <div class="text-caption font-weight-bold mb-1">Rollen</div>
                <div class="d-flex flex-wrap ga-1 mb-3">
                    <v-chip
                        v-for="role in availableRoles"
                        :key="role.id"
                        :color="simulatedRoles.includes(role.id) ? 'primary' : 'default'"
                        :variant="simulatedRoles.includes(role.id) ? 'flat' : 'outlined'"
                        size="small"
                        @click="toggleRole(role.id)"
                    >
                        {{ role.name }} ({{ role.id }})
                    </v-chip>
                </div>

                <!-- Service Simulation -->
                <div class="text-caption font-weight-bold mb-1">Services</div>
                <div class="d-flex flex-wrap ga-1 mb-2">
                    <v-chip
                        v-for="svc in availableServices"
                        :key="svc"
                        :color="simulatedServices.includes(svc) ? 'success' : 'default'"
                        :variant="simulatedServices.includes(svc) ? 'flat' : 'outlined'"
                        size="small"
                        @click="toggleService(svc)"
                    >
                        {{ svc }}
                    </v-chip>
                </div>

                <!-- Active User Info -->
                <v-divider class="my-2"/>
                <div class="text-caption text-medium-emphasis">
                    <template v-if="isTabOverride">
                        <v-icon size="x-small" color="deep-purple" class="mr-1">mdi-account-multiple</v-icon>
                        Tab-User: <strong>{{ tabUser }}</strong>
                    </template>
                    <template v-else-if="simulatedUser">
                        <v-icon size="x-small" color="warning" class="mr-1">mdi-account-switch</v-icon>
                        Simuliert: <strong>{{ simulatedUser }}</strong>
                    </template>
                    <template v-else>
                        <v-icon size="x-small" class="mr-1">mdi-account</v-icon>
                        Kein User-Override aktiv
                    </template>
                </div>
            </v-card-text>
        </v-card>
    </div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, ref, watch} from 'vue'
import axios from 'axios'
import {useApiCredentials} from '@/composables/useApiCredentials'
import {useAuthorizationStore} from '@/stores/AuthorizationStore/AuthorizationStore'
import {storeToRefs} from 'pinia'
import {ADMIN_ROLE, APP_ADMIN_ROLE} from '@/composables/useAppConst'

// Separate axios instance WITHOUT the simulation interceptor so that
// GET /dev/context always reaches the backend as the real admin user.
const rawAxios = axios.create({
    baseURL: (import.meta.env.VITE_BASE_URL || '') + (import.meta.env.VITE_API_URL || '/api'),
})
rawAxios.defaults.withCredentials = true

const STORAGE_KEY = 'dev-simulator'
const TAB_STORAGE_KEY = 'dev-simulator-tab'

interface DevUserInfo {
    id: number
    username: string
    use_case_id: number | null
    services: string[] | null
}

interface DevRoleInfo {
    id: number
    name: string
}

interface SimState {
    user: string | null
    roles: number[]
    services: string[]
    manualRoleSelection?: boolean
    manualServiceSelection?: boolean
}

function loadState(): SimState {
    // Per-tab override (sessionStorage) takes precedence
    try {
        const tabRaw = sessionStorage.getItem(TAB_STORAGE_KEY)
        if (tabRaw) {
            const parsed = JSON.parse(tabRaw) as Partial<SimState>
            return {
                user: parsed.user ?? null,
                roles: Array.isArray(parsed.roles) ? parsed.roles : [],
                services: Array.isArray(parsed.services) ? parsed.services : [],
                manualRoleSelection: Boolean(parsed.manualRoleSelection),
                manualServiceSelection: Boolean(parsed.manualServiceSelection),
            }
        }
    } catch { /* ignore */ }
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
            const parsed = JSON.parse(raw) as Partial<SimState>
            return {
                user: parsed.user ?? null,
                roles: Array.isArray(parsed.roles) ? parsed.roles : [],
                services: Array.isArray(parsed.services) ? parsed.services : [],
                manualRoleSelection: Boolean(parsed.manualRoleSelection),
                manualServiceSelection: Boolean(parsed.manualServiceSelection),
            }
        }
    } catch { /* ignore */ }
    return {
        user: null,
        roles: [],
        services: [],
        manualRoleSelection: false,
        manualServiceSelection: false,
    }
}

function saveState(state: SimState) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

function arraysEqual(a: unknown[], b: unknown[]): boolean {
    if (a.length !== b.length) return false
    const sa = [...a].sort()
    const sb = [...b].sort()
    return sa.every((v, i) => v === sb[i])
}

export default defineComponent({
    name: 'DevToolbar',
    setup() {
        const isDev = import.meta.env.VITE_MODE !== 'production'
        const expanded = ref(false)
        const {guid, axiosBearerHeader} = useApiCredentials()
        const authStore = useAuthorizationStore()
        const {roles: storeRoles, services: storeServices, isAppAdmin, isGlobalAdmin} = storeToRefs(authStore)

        const availableUsers = ref<DevUserInfo[]>([])
        const availableRoles = ref<DevRoleInfo[]>([])
        const availableServices = ref<string[]>([])
        const newTabUser = ref<string | null>(null)

        // Track the last guid for which we fetched context to detect app switches
        const lastFetchedGuid = ref<string | null>(null)

        // Per-tab override state
        const tabOverrideRaw = sessionStorage.getItem(TAB_STORAGE_KEY)
        const isTabOverride = ref(!!tabOverrideRaw)
        const tabUser = ref<string | null>(null)
        if (tabOverrideRaw) {
            try {
                tabUser.value = JSON.parse(tabOverrideRaw).user
            } catch { /* ignore */ }
        }

        const saved = loadState()
        const simulatedUser = ref<string | null>(saved.user)
        const manualRoleSelection = ref(Boolean(saved.manualRoleSelection))
        const manualServiceSelection = ref(Boolean(saved.manualServiceSelection))
        const simulatedRoles = ref<number[]>(manualRoleSelection.value ? saved.roles : [])
        const simulatedServices = ref<string[]>(manualServiceSelection.value ? saved.services : [])

        const persistState = () => {
            saveState({
                user: simulatedUser.value,
                roles: simulatedRoles.value,
                services: simulatedServices.value,
                manualRoleSelection: manualRoleSelection.value,
                manualServiceSelection: manualServiceSelection.value,
            })
        }

        const hasSimulation = computed(() =>
            simulatedRoles.value.length > 0 || simulatedServices.value.length > 0,
        )

        const fetchContext = async () => {
            if (!guid.value) return
            try {
                const {data} = await rawAxios.get(
                    `/${guid.value}/dev/context`,
                    axiosBearerHeader.value,
                )
                const isAppSwitch = lastFetchedGuid.value !== null
                    && lastFetchedGuid.value !== guid.value
                lastFetchedGuid.value = guid.value

                availableUsers.value = data.users ?? []
                availableRoles.value = data.roles ?? []
                availableServices.value = data.services ?? []

                // On app switch: reset simulation to the new app's actual state
                if (isAppSwitch) {
                    simulatedRoles.value = [...(data.current_permissions ?? [])]
                    simulatedServices.value = [...(data.app_services ?? data.services ?? [])]
                    manualRoleSelection.value = false
                    manualServiceSelection.value = false
                    persistState()
                    applyToStore()
                    return
                }

                // First load: initialize with current permissions if no simulation active
                if (!hasSimulation.value) {
                    simulatedRoles.value = [...(data.current_permissions ?? storeRoles.value ?? [])]
                    simulatedServices.value = [...(data.app_services ?? storeServices.value ?? [])]
                    manualRoleSelection.value = false
                    manualServiceSelection.value = false
                    persistState()
                }
            } catch (e) {
                console.warn('DevToolbar: could not fetch dev context', e)
            }
        }

        const applyToStore = () => {
            if (simulatedRoles.value.length > 0) {
                storeRoles.value = [...simulatedRoles.value]
                isAppAdmin.value = simulatedRoles.value.includes(APP_ADMIN_ROLE) || simulatedRoles.value.includes(ADMIN_ROLE)
                isGlobalAdmin.value = simulatedRoles.value.includes(ADMIN_ROLE)
            }
            if (simulatedServices.value.length > 0) {
                storeServices.value = [...simulatedServices.value]
            }
        }

        // Re-apply simulation whenever external code (e.g. currentApp())
        // overwrites the store values.  The equality check prevents loops.
        // Also re-fetch context after app switch (storeRoles updates AFTER
        // token re-acquisition + currentApp(), so the new bearer token is ready).
        // Guard: only register in dev mode — in production isDev is false and
        // the watcher would fire needlessly hitting a 404 on /dev/context.
        if (isDev) {
            watch([storeRoles, storeServices], () => {
                // Re-fetch context when store permissions change from an app switch.
                // guid.value has already changed at this point and the new bearer
                // token is available — safe to call fetchContext().
                if (guid.value && lastFetchedGuid.value !== guid.value) {
                    fetchContext()
                }

                if (!hasSimulation.value) return
                const rolesMatch = arraysEqual(storeRoles.value ?? [], simulatedRoles.value)
                const servicesMatch = arraysEqual(storeServices.value ?? [], simulatedServices.value)
                if (!rolesMatch || !servicesMatch) {
                    applyToStore()
                }
            })
        }

        const onSimulationChange = () => {
            persistState()
            applyToStore()
        }

        const toggleRole = (roleId: number) => {
            const idx = simulatedRoles.value.indexOf(roleId)
            if (idx >= 0) {
                simulatedRoles.value.splice(idx, 1)
            } else {
                simulatedRoles.value.push(roleId)
            }
            manualRoleSelection.value = simulatedRoles.value.length > 0
            onSimulationChange()
        }

        const toggleService = (svc: string) => {
            const idx = simulatedServices.value.indexOf(svc)
            if (idx >= 0) {
                simulatedServices.value.splice(idx, 1)
            } else {
                simulatedServices.value.push(svc)
            }
            manualServiceSelection.value = simulatedServices.value.length > 0
            onSimulationChange()
        }

        const openAsUserInNewTab = () => {
            if (!newTabUser.value) return
            // Open current page in a new tab with ?devUser= query param
            // main.ts picks this up, stores in sessionStorage, and removes the param
            const url = new URL(window.location.href)
            url.searchParams.set('devUser', newTabUser.value)
            window.open(url.toString(), '_blank')
            newTabUser.value = null
        }

        const clearTabOverride = () => {
            sessionStorage.removeItem(TAB_STORAGE_KEY)
            isTabOverride.value = false
            tabUser.value = null
            // Reload to fall back to localStorage simulation
            window.location.reload()
        }

        const resetAll = () => {
            simulatedUser.value = null
            simulatedRoles.value = []
            simulatedServices.value = []
            manualRoleSelection.value = false
            manualServiceSelection.value = false
            localStorage.removeItem(STORAGE_KEY)
            sessionStorage.removeItem(TAB_STORAGE_KEY)
            isTabOverride.value = false
            tabUser.value = null
            // Refetch real state from backend
            authStore.currentApp()
        }

        // NOTE: We do NOT watch guid directly for fetchContext because it
        // fires before AssignedApplicationPicker re-acquires the bearer
        // token for the new app.  Instead, the [storeRoles, storeServices]
        // watcher above triggers fetchContext after currentApp() completes
        // (which is after token acquisition).

        onMounted(() => {
            if (isDev) {
                // Apply saved simulation state SYNCHRONOUSLY so that
                // sibling components (e.g. DashboardView) see the
                // correct roles/services on their own onMounted.
                if (saved.roles.length > 0 || saved.services.length > 0) {
                    applyToStore()
                }
                if (guid.value) {
                    fetchContext()
                }
            }
        })

        return {
            isDev,
            expanded,
            availableUsers,
            availableRoles,
            availableServices,
            simulatedUser,
            simulatedRoles,
            simulatedServices,
            newTabUser,
            isTabOverride,
            tabUser,
            toggleRole,
            toggleService,
            onSimulationChange,
            openAsUserInNewTab,
            clearTabOverride,
            resetAll,
        }
    },
})
</script>

<style scoped lang="scss">
.dev-toolbar-wrapper {
    position: fixed;
    bottom: 16px;
    right: 16px;
    z-index: 9999;
}

.dev-toolbar-fab {
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover {
        opacity: 1;
    }
}

.dev-toolbar-card {
    max-height: 80vh;
    overflow-y: auto;
}
</style>